// =============================================================================
// UnleashedPushListAdmin.jsx — Manage → DFV Services → "DFV Services Push List"
// =============================================================================
// The admin view over unleashed_push_queue (db-133): every pending / recent
// change queued for the DFV Services -> Unleashed auto-sync worker. Lets an admin
// WATCH the queue (which service, what kind of change, its status) and manage it:
// delete a bad/superseded queued change, or retry an errored one. Carries a
// convenience on/off switch for the worker (mirrors Manage → System Setup →
// Unleashed, its official home) so testing/monitoring can happen in one place.
//
// The worker only auto-pushes Zephyr* services; a change to a TEST/non-Zephyr
// service is never enqueued, so it won't appear here.
// =============================================================================
import React from "react";
import { getConfigValue, upsertConfig } from "../../data/portalConfig";
import { listShelterSyncInfoByIds } from "../../data/shelters";
import { cancelQueueJob, listQueueJobs, requeueQueueJob } from "../../data/unleashedPushQueue";
import { listMembershipSummariesByIds } from "../../data/userShelters";
import { usePageSize } from "../../hooks/usePageSize";
import { confirmDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { Btn, SlideToggle } from "../../utils/ui.jsx";
import { PageNav } from "../common/PageNav.jsx";

const SYNC_KEY = "unleashed_customer_sync_enabled";

const STATUS_BADGE = {
  queued: "bg-[#E3F2FD] text-[#1565C0]",
  processing: "bg-[#FFF8E1] text-[#F57F17]",
  done: "bg-[#E8F5E9] text-[#2E7D32]",
  error: "bg-[#FFEBEE] text-[#C62828]",
  hold: "bg-[#ECEFF1] text-[#546E7A]",
  cancelled: "bg-[#ECEFF1] text-[#90A4AE]",
};

const FILTERS = ["all", "queued", "processing", "error", "done", "hold", "cancelled"];

function fmt(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

// One label/value row in the inspect modal.
function Field({ label, value, mono }) {
  return (
    <div className="flex py-[6px] border-b border-zephyr-border last:border-b-0 text-[13px]">
      <div className="w-[150px] shrink-0 text-zephyr-muted">{label}</div>
      <div className={`flex-1 text-zephyr-text break-words ${mono ? "font-mono text-[12px]" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export function UnleashedPushListAdmin() {
  const [jobs, setJobs] = React.useState([]);
  const [shelters, setShelters] = React.useState({}); // id -> { code, name }
  const [memberships, setMemberships] = React.useState({}); // membership id -> summary (contact rows)
  const [selected, setSelected] = React.useState(null); // job open in the inspect modal
  const [loading, setLoading] = React.useState(true);
  const [syncOn, setSyncOn] = React.useState(false);
  const [syncBusy, setSyncBusy] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const [msg, setMsg] = React.useState("");
  const [pageSize] = usePageSize();
  const [page, setPage] = React.useState(0);
  const [search, setSearch] = React.useState("");

  const load = React.useCallback(async function () {
    setLoading(true);
    const rows = await listQueueJobs(12500);
    const on = await getConfigValue(SYNC_KEY);
    setSyncOn(on === true);
    const list = rows ?? [];
    setJobs(list);
    // Contact rows resolve via their membership (entity_id = user_shelters.id).
    const contactIds = [
      ...new Set(list.filter((j) => j.entity_type === "contact").map((j) => j.entity_id)),
    ];
    const memRows = contactIds.length ? (await listMembershipSummariesByIds(contactIds)) || [] : [];
    const memMap = {};
    for (const m of memRows) memMap[m.id] = m;
    setMemberships(memMap);
    // Shelters: customer entity_ids + the contact memberships' shelter_ids.
    const custIds = list.filter((j) => j.entity_type === "customer").map((j) => j.entity_id);
    const shelterIds = [
      ...new Set([...custIds, ...memRows.map((m) => m.shelter_id).filter(Boolean)]),
    ];
    if (shelterIds.length) {
      const shs = await listShelterSyncInfoByIds(shelterIds);
      const map = {};
      for (const s of shs ?? []) {
        map[s.id] = { code: s.shelter_code, name: s.shelter_name, guid: s.unleashed_guid || null };
      }
      setShelters(map);
    } else {
      setShelters({});
    }
    setLoading(false);
  }, []);

  React.useEffect(
    function () {
      load();
    },
    [load]
  );

  async function toggleSync() {
    const turnOn = !syncOn;
    const ok = await confirmDialog({
      title: turnOn ? "Turn on customer auto-sync" : "Turn off customer auto-sync",
      message: turnOn
        ? "Turn on automatic customer sync? Queued changes to Zephyr DFV Services will start being pushed to Unleashed on the next run."
        : "Turn off automatic customer sync? Changes will keep queuing here and be sent when you turn it back on (nothing is lost).",
      confirmLabel: turnOn ? "Turn on" : "Turn off",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    setSyncBusy(true);
    const res = await upsertConfig(SYNC_KEY, turnOn);
    setSyncBusy(false);
    if (res.error) {
      setMsg(`Failed: ${res.error.message}`);
      return;
    }
    logAudit("unleashed_customer_sync_toggled", { enabled: turnOn, via: "push_list" });
    setSyncOn(turnOn);
    setMsg(turnOn ? "Customer auto-sync is ON ✓" : "Customer auto-sync is OFF ✓");
    setTimeout(function () {
      setMsg("");
    }, 3000);
  }

  async function cancelJob(job) {
    const ok = await confirmDialog({
      title: "Cancel queued change?",
      message:
        "Cancel this queued push? It won't be sent to Unleashed, but the record is kept (marked Cancelled) rather than deleted. A later change to the same DFV Service will queue a fresh push.",
      confirmLabel: "Cancel change",
      cancelLabel: "Keep",
      danger: true,
    });
    if (!ok) return;
    const res = await cancelQueueJob(job.id);
    if (res.error) {
      setMsg(`Cancel failed: ${res.error.message}`);
      return;
    }
    load();
  }

  async function retry(job) {
    const res = await requeueQueueJob(job.id);
    if (res.error) {
      setMsg(`Retry failed: ${res.error.message}`);
      return;
    }
    load();
  }

  const base = filter === "all" ? jobs : jobs.filter((j) => j.status === filter);
  const q = search.trim().toLowerCase();
  const shown = q ? base.filter((j) => jobText(j).includes(q)) : base;
  // Pagination over the filtered set (shared per-device page size, services/pageSize).
  const pageCount = Math.max(1, Math.ceil(shown.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageJobs = shown.slice(safePage * pageSize, (safePage + 1) * pageSize);
  // Jump back to the first page whenever the filter changes (the row set is different).
  React.useEffect(
    function () {
      setPage(0);
    },
    [filter, search]
  );

  function personName(m) {
    return [m.first_name, m.last_name].filter(Boolean).join(" ").trim() || m.email || "(contact)";
  }
  function serviceCodeFor(m) {
    const sh = m.shelter_id ? shelters[m.shelter_id] : null;
    return sh ? sh.code : m.shelter_code || "";
  }
  function serviceLabel(job) {
    if (job.entity_type === "customer") {
      const s = shelters[job.entity_id];
      return s ? `${s.code} — ${s.name}` : job.entity_id;
    }
    // contact: entity_id is a user_shelters membership id → person + service.
    const m = memberships[job.entity_id];
    if (!m) return job.entity_id;
    const code = serviceCodeFor(m);
    return code ? `${personName(m)} — ${code}` : personName(m);
  }

  // Free-text search haystack for one job — everything someone might type to find it:
  // service code + name, contact person + email, and the job id. Uses the already-
  // resolved shelters/memberships maps, so no extra query.
  function jobText(job) {
    const parts = [String(job.id)];
    if (job.entity_type === "customer") {
      const s = shelters[job.entity_id];
      if (s) parts.push(s.code, s.name);
    } else {
      const m = memberships[job.entity_id];
      if (m) {
        parts.push(m.first_name, m.last_name, m.email, m.shelter_code);
        const sh = m.shelter_id ? shelters[m.shelter_id] : null;
        if (sh) parts.push(sh.code, sh.name);
      }
    }
    return parts.filter(Boolean).join(" ").toLowerCase();
  }

  // Resolved link details for the inspect modal (null when nothing is open).
  const selM =
    selected && selected.entity_type === "contact" ? memberships[selected.entity_id] : null;
  // The Unleashed CUSTOMER GUID relevant to the open job: its own (customer job) or its
  // parent's (contact job). Null = not linked → a push CREATES rather than updates.
  function customerGuidForSelected() {
    if (!selected) return null;
    if (selected.entity_type === "customer") return shelters[selected.entity_id]?.guid || null;
    return selM?.shelter_id ? shelters[selM.shelter_id]?.guid || null : null;
  }
  const selCustomerGuid = customerGuidForSelected();
  function contactPushIntent() {
    if (selM?.unleashed_contact_guid) return "Update the contact";
    if (selCustomerGuid) return "Create the contact";
    return "Hold until the Customer exists";
  }

  return (
    <div className="mx-auto p-4" style={{ maxWidth: "var(--page-max)" }}>
      <h2 className="m-0 mb-1 text-[20px] font-bold text-zephyr-text">DFV Services Push List</h2>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        Changes to Zephyr DFV Services waiting to sync to Unleashed. Delete a queued change to skip
        it, or retry one that errored. TEST / non-Zephyr services are never auto-pushed, so they
        never appear here.
      </p>

      {/* On/off + refresh */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <SlideToggle
          checked={syncOn}
          disabled={syncBusy}
          size="lg"
          onChange={function () {
            if (!syncBusy) toggleSync();
          }}
          label={syncOn ? "Auto-sync: ON" : "Auto-sync: OFF"}
        />
        <Btn onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </Btn>
        {msg && (
          <span
            className={`text-[13px] font-semibold ${
              msg.startsWith("Failed") || msg.includes("failed")
                ? "text-[#C62828]"
                : "text-[#2E7D32]"
            }`}
          >
            {msg}
          </span>
        )}
      </div>

      {/* Status filter + free-text search */}
      <div className="flex items-center gap-2 mb-3 flex-wrap justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map(function (f) {
            const n = f === "all" ? jobs.length : jobs.filter((j) => j.status === f).length;
            return (
              <button
                key={f}
                type="button"
                onClick={function () {
                  setFilter(f);
                }}
                className={`px-3 py-1 rounded-full text-[12px] border ${
                  filter === f
                    ? "bg-zephyr-teal text-white border-zephyr-teal"
                    : "bg-white text-zephyr-text border-zephyr-border"
                }`}
              >
                {f} ({n})
              </button>
            );
          })}
        </div>
        <input
          type="search"
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          placeholder="Search service, person, email, job id…"
          aria-label="Search the push list"
          className="px-3 py-1 border border-zephyr-border rounded text-[13px] min-w-[240px]"
        />
      </div>

      {loading ? (
        <p className="text-[14px] text-zephyr-muted">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-[14px] text-zephyr-muted">
          {search.trim() ? "No jobs match your search." : "Nothing queued."}
        </p>
      ) : (
        <div className="overflow-x-auto border border-zephyr-border rounded">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-zephyr-panel-bg text-left">
                <th className="p-2 font-semibold">Queued</th>
                <th className="p-2 font-semibold">DFV Service</th>
                <th className="p-2 font-semibold">Job ID</th>
                <th className="p-2 font-semibold">Change</th>
                <th className="p-2 font-semibold">Status</th>
                <th className="p-2 font-semibold text-center">Attempts</th>
                <th className="p-2 font-semibold">Last error</th>
                <th className="p-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageJobs.map(function (job) {
                return (
                  <tr key={job.id} className="border-t border-zephyr-border align-top">
                    <td className="p-2 whitespace-nowrap text-zephyr-muted">
                      {fmt(job.created_at)}
                    </td>
                    <td className="p-2">{serviceLabel(job)}</td>
                    <td className="p-2 whitespace-nowrap font-mono text-[12px] text-zephyr-muted">
                      {job.id}
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      {job.op === "delete" ? "Remove" : "Update"} ({job.entity_type})
                    </td>
                    <td className="p-2">
                      <span
                        className={`inline-block px-2 py-[2px] rounded-full text-[11px] font-semibold ${
                          STATUS_BADGE[job.status] || "bg-[#ECEFF1] text-[#546E7A]"
                        }`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="p-2 text-center">{job.attempts ?? 0}</td>
                    <td className="p-2 text-[#C62828] max-w-[280px] break-words">
                      {job.last_error || ""}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={function () {
                          setSelected(job);
                        }}
                        className="text-zephyr-teal underline mr-3"
                      >
                        Inspect
                      </button>
                      {job.status === "error" && (
                        <button
                          type="button"
                          onClick={function () {
                            retry(job);
                          }}
                          className="text-zephyr-teal underline mr-3"
                        >
                          Retry
                        </button>
                      )}
                      {/* A CANCELLED job can be brought back (cancelled by mistake, or the
                          underlying data was fixed) — re-queue it for the worker. */}
                      {job.status === "cancelled" && (
                        <button
                          type="button"
                          onClick={function () {
                            retry(job);
                          }}
                          className="text-zephyr-teal underline mr-3"
                        >
                          Re-queue
                        </button>
                      )}
                      {job.status !== "done" && job.status !== "cancelled" && (
                        <button
                          type="button"
                          onClick={function () {
                            cancelJob(job);
                          }}
                          className="text-[#C62828] underline"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && shown.length > 0 && (
        <PageNav page={safePage} pageCount={pageCount} onPage={setPage} />
      )}

      {selected && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl"
            style={{ maxWidth: 560, width: "100%", maxHeight: "85vh", overflowY: "auto" }}
          >
            <div className="flex justify-between items-center border-b border-zephyr-border p-4">
              <div className="font-bold text-[16px] text-zephyr-teal-dark">Queued change</div>
              <button
                type="button"
                onClick={function () {
                  setSelected(null);
                }}
                className="bg-transparent border-0 text-[20px] cursor-pointer text-zephyr-muted"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <Field
                label="Change"
                value={`${selected.op === "delete" ? "Remove" : "Update"} (${selected.entity_type})`}
              />
              <Field label="Subject" value={serviceLabel(selected)} />

              {selected.entity_type === "customer" && (
                <>
                  <Field
                    label="Unleashed GUID"
                    value={selCustomerGuid || "— not linked"}
                    mono={!!selCustomerGuid}
                  />
                  <Field
                    label="This push will"
                    value={
                      selCustomerGuid
                        ? "Update the existing Customer"
                        : "Create a new Customer (fails if the code already exists in Unleashed — run Link Customers first)"
                    }
                  />
                </>
              )}

              {selected.entity_type === "contact" &&
                (selM ? (
                  <>
                    <Field label="Email" value={selM.email || "—"} />
                    <Field label="Role" value={selM.contact_label || "—"} />
                    <Field
                      label="Contact GUID"
                      value={selM.unleashed_contact_guid || "— not linked"}
                      mono={!!selM.unleashed_contact_guid}
                    />
                    <Field
                      label="Parent Customer"
                      value={
                        selCustomerGuid
                          ? "Linked"
                          : "Not linked — this contact holds until the Customer syncs"
                      }
                    />
                    <Field label="This push will" value={contactPushIntent()} />
                  </>
                ) : (
                  <Field
                    label="Note"
                    value="This membership no longer exists (removed/recreated) — this queued change is stale and will resolve as a no-op."
                  />
                ))}
              <Field label="Status" value={selected.status} />
              <Field label="Attempts" value={String(selected.attempts ?? 0)} />
              <Field label="Queued" value={fmt(selected.created_at)} />
              <Field label="Updated" value={fmt(selected.updated_at)} />
              {selected.last_error && <Field label="Last error" value={selected.last_error} />}
              {Array.isArray(selected.payload?.changes) && selected.payload.changes.length > 0 && (
                <Field
                  label="Changed"
                  value={selected.payload.changes
                    .map((c) => `${c.label}: ${c.from} → ${c.to}`)
                    .join("; ")}
                />
              )}
              {selected.payload?.contact_guid && (
                <Field
                  label="Detach GUIDs"
                  value={`customer ${selected.payload.customer_guid || "—"}, contact ${selected.payload.contact_guid}`}
                />
              )}
              <Field label="Record id" value={selected.entity_id} mono />
              <Field label="Job id" value={String(selected.id)} mono />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
